// All game content lives here, so the studio page and the deep-dive pages stay in sync.
// Texts marked "draft" were written from the design docs: Florin should rewrite them in his own words.

import { defineAsyncComponent, markRaw } from 'vue';

// Desk Pond demo installer (from `npm run release:demo`). Change only this line for a new demo version.
// Never link the full game installer here.
const DESK_POND_DEMO = 'https://florin-lica.com/Outside/DeskPond/demo/DeskPond-Demo-Setup-0.2.0.exe';
// Where the full game is sold.
const DESK_POND_BUY = 'https://fluorinerepublika.itch.io/desk-pond';

const games = [
  {
    slug: 'desk-pond',
    name: 'Desk Pond',
    codename: true,
    tagline: 'A tiny pond at the bottom of your second monitor, where frogs sing a lily bud into bloom while you work.',
    howYouPay: 'Free demo. The full game is $2.99, paid once, and your demo save carries over.',
    pricing: { price: 'Free demo, full game $2.99', payFor: 'The full game, once' },
    platforms: ['Windows'],
    status: 'In development',
    image: require('@/assets/images/games/desk-pond-icon.png'),
    imageFit: 'contain',
    pitch:
      'Your frogs sing to a lily bud while you work. Click now and then, collect thousands of frogs, and look up when a golden butterfly drifts by. The window is transparent and clicks through everywhere except the pond, so it sits at the edge of your vision and never gets in your way.',
    taught:
      'My first PC game: transparent always-on-top windows, an idle-game economy that has to feel fair whether you click a lot or never, and shipping a Windows installer with auto-updates.',
    links: [
      { label: 'Try the demo', url: DESK_POND_DEMO },
      { label: 'Buy the full game, $2.99 on itch.io', url: DESK_POND_BUY }
    ],
    features: [
      { title: 'Grow blooms', text: 'Pond frogs sing notes to the bud. Full buds bloom and pay gold; every 10th bud is a Moon Lily that brings a new frog egg.' },
      { title: 'Collect 5,760 frogs', text: '12 base colours, patterns, accents and eyes, rarity tiers from Common to Legendary, and breeding with mutations.' },
      { title: 'Hop', text: 'A lily-pad minigame: hop across sinking pads, catch flies, meet wild frogs and breed with them.' },
      { title: 'Day and night', text: 'The real clock decides: a flower book by day, 40 constellations and fireflies by night.' },
      { title: 'Seasons', text: 'When a run gets long, let winter come. The pond freezes, comes back in spring, and pays Moon Seeds for lasting perks.' }
    ],
    // draft
    lore:
      'A small pond at the edge of your screen, where frogs sing a lily bud into bloom. By day the bud grows and flowers; at night it sleeps and the sky above the pond fills with constellations. Every Season the pond freezes and comes back in spring, and the oldest frogs return as Elders with a mossy crown.',
    // The Desk Pond page shows these as an infographic (components/games/DeskPondStory.vue), not as cards.
    development: [],
    ux: [],
    economy: [],
    roadmap: null,
    gallery: [
      require('@/assets/images/games/desk-pond-stats.jpg')
    ],
    story: {
      component: markRaw(defineAsyncComponent(() => import('@/components/games/DeskPondStory.vue'))),
      dayNight: {
        day: require('@/assets/images/games/desk-pond-day.jpg'),
        night: require('@/assets/images/games/desk-pond-night.jpg'),
        dayAlt: 'Desk Pond by day: eight frogs on lily pads, a nest on the left and a pink lily bud on its own pad on the right.',
        nightAlt: 'The same pond at night: darker water, fireflies, a crescent moon, and a constellation drawing itself above the sleeping bud.',
        text: 'The real clock decides. From the evening (20:00 by default, 18:00 to 23:00 in Settings) until 07:00, the bud falls asleep and a constellation draws itself above it, line by line, as you grow it. Same progress, different picture, and 40 constellations to find.'
      },
      steps: [
        { label: 'Idea', title: 'From one chameleon to a pond', text: 'A 2025 concept had a single chameleon catching bugs on the taskbar. A pond of frogs kept the bug-catching and made a four-trait collection feel natural.' },
        { label: 'Pivot', title: 'Something to push against', text: 'Gold ticking up didn\'t feel like anything. I wanted a Clicker Heroes-style target without combat, so a lily bud became the thing you grow.' },
        { label: 'Prototype', title: 'A strip that stays out of the way', text: 'A transparent, always-on-top Electron strip along the bottom of the screen. Frogs and buds are generated from parts, so no stage needs hand-made art.' },
        { label: 'Playtest', title: 'Living with it all day', text: 'Running it on my second monitor found the real problems: a bud lost among the frogs, numbers too fast to read, eggs that vanished, flies that only visited the middle frogs.' },
        { label: 'Balance', title: 'A simulator, not a hunch', text: 'A small simulator replays weeks of play in seconds. It checks that clicking and idling end up close, and that the late game never stalls.' },
        { label: 'Ship', title: 'Ready for real players', text: 'An installer with auto-updates, saves that survive reinstalls, and debug tools that never ship. Now at version 0.1.6, heading for the Microsoft Store and the Mac App Store.' }
      ],
      pivots: {
        text: 'I wanted a target to push against, like the monster in Clicker Heroes, but in a game that sits quietly next to your work. These ideas lost along the way:',
        dropped: [
          { title: 'A monster to defeat', why: 'Clicking a boss for damage doesn\'t fit a cozy game that lives on your desktop.', quote: 'Maybe a target to attack doesn\'t fit a cozy desktop game.' },
          { title: 'A frog village', why: 'Tiny houses along the whole taskbar would take over the screen, and at the time the window couldn\'t even click through its empty space.' },
          { title: 'A river journey', why: 'Every stretch and every boss would need hand-made art. Ten stages in, it runs out.', quote: 'I liked journey at first, but it\'s not scalable.' }
        ],
        kept: [
          { mark: '✓', title: 'Blooms', why: 'One bud at a time, drawn from parts picked by its stage, so bud 500 needs no new art. Every 10th bud is a Moon Lily that brings a new frog.' },
          { mark: '↺', title: 'The starry sky came back', why: 'A runner-up idea returned as the night version of the bud: the same progress, drawn as constellations.' }
        ]
      },
      ux: [
        { id: 'clickthrough', title: 'Clicks that go through', before: 'The window was a rectangle: its empty, see-through space still blocked clicks.', after: 'Click-through follows the drawn shapes, so you can click your desktop right next to a frog.' },
        { id: 'bud', title: 'Where do the frogs end?', before: 'The bud sat in the row of frogs and blended in.', after: 'A reed clump, a gap and its own lotus pad give the bud a spot of its own.' },
        { id: 'stats', title: 'Less on screen', before: 'A stats bar along the pond, always full of numbers.', after: 'Two small buttons. Windows grow out of their button and close when you click away.' },
        { id: 'breeding', title: 'Breeding in one place', before: 'Breeding picks floated in a bare row above the nest.', after: 'Breeding lives in a footer inside the Frogs window, next to the frogs you pick from.' },
        { id: 'chorus', title: 'Aiming should matter', before: 'A caught fly sped up the whole pond, so it didn\'t matter which frog caught it.', after: 'The frog that catches the fly sings ×2 and glows gold, with its own countdown.' }
      ],
      uxNotes: [
        { title: 'Numbers you can read.', text: 'The taps-per-second stat jumped too fast. It now uses a 5-second window, refreshes once a second and eases between values.' },
        { title: 'Rewards never get lost.', text: 'Eggs earned while the nursery is full wait in line, shown on the nest as "+N waiting".' },
        { title: 'A stray click is not a disaster.', text: 'A Hop run goes on hold instead of ending when you click away to your work.' }
      ],
      economy: {
        runsTitle: 'Clicking vs idling',
        runsAlt: 'Line chart of the bud reached over time for three players. The active player reaches bud 10 in 7 minutes and the idle one in 37, but they reach bud 75 after 28.5 and 38.3 hours.',
        runs: [
          { name: 'active', label: 'Clicks a lot: 30 taps every minute', color: '#E8B923', points: [[0.12, 10], [0.28, 20], [0.57, 30], [1.2, 40], [3.0, 50], [28.5, 75]] },
          { name: 'casual', label: 'Casual: 5 taps every 5 minutes', color: '#4EC9AC', points: [[0.43, 10], [0.8, 20], [1.5, 30], [2.5, 40], [5.6, 50], [33.5, 75]] },
          { name: 'idle', label: 'Idle: looks in every 30 minutes, no taps', color: '#4F96C9', points: [[0.62, 10], [1.5, 20], [2.6, 30], [4.1, 40], [7.3, 50], [38.3, 75]] }
        ],
        runsText: 'Clicking a lot reaches bud 10 in 7 minutes; an idle pond takes 37. By bud 75 they are only about 10 hours apart, and all three reach bud 100 in about 10 days. Numbers from the game\'s balance simulator.',
        curveTitle: 'Why each bud takes a bit longer',
        curveAlt: 'Two curves on a log scale over buds 1 to 40: growth needed rises by 1.25 per bud, gold paid by 1.22 per bud, and the gap between them widens.',
        growthRate: 1.25,
        goldRate: 1.22,
        curveText: 'Each bud needs ×1.25 more growth but pays only ×1.22 more gold. By bud 40 a bud asks {x}× more growth for its gold than the first one did. That gentle brake keeps a burst of clicking from running away from the pacing.',
        facts: [
          { big: '20%', text: 'Offline growth, for up to 8 hours. The game rewards having the pond open, not leaving it closed.' },
          { big: '0', text: 'Events while the game is closed: no butterflies or swarms pile up while you are away.' },
          { big: '×1.07', text: 'More Moon Seeds for every bud past 75, so a long run is never a trap.' },
          { big: '~40', text: 'New frogs per Season after the late-game pass, up from about 27.' }
        ]
      },
      frogs: {
        text: 'Every frog is drawn from its traits, not by hand, so the whole collection exists without 5,760 drawings. Base colour carries no power, so you pick the colours you like. The pattern sets strength, the accent gives a role, and rarity scales that role from ×1 to ×3.'
      },
      roadmap: {
        now: [
          'A see-through pond strip that clicks through to your desktop',
          'Blooms, Moon Lilies and offline growth',
          '5,760 frogs: eggs, nests, breeding and Hop',
          'Flower book by day, 40 constellations by night',
          'Seasons with Moon Seeds and Elders',
          'Missions, a first-time tour and auto-updates'
        ],
        moreNow: 18,
        next: [
          'Season frogs: one unique frog for each Season',
          'Hop hazards (pike, heron shadow) and power-ups',
          'Soft sounds, off by default',
          'Balance and wording passes after playtesting',
          'Release on the Microsoft Store and the Mac App Store (no date yet)'
        ],
        later: [
          'Better frog art and hopping animations',
          'Frog races, where fast bloodlines pay off',
          'Trade a single frog with a friend',
          'Pond decorations: reeds, rocks, a little bridge',
          'Frog hats. Obviously.'
        ]
      }
    },
    privacy: ''
  },
  {
    slug: 'project-lantern',
    name: 'Project Lantern',
    codename: true,
    // draft
    tagline: 'A text adventure told through chat messages. Someone is writing to you, and your replies decide what happens.',
    howYouPay: 'Chapter 1 is free. Later chapters are bought once, the price is shown up front, and they stay on your account.',
    pricing: { price: 'Chapter 1 free, later chapters priced up front', payFor: 'New chapters, yours to keep' },
    platforms: ['Mobile'],
    status: 'In development',
    image: null,
    imageFit: 'cover',
    // draft
    pitch:
      'Dez went down through a trapdoor in the band\'s basement after a lost guitar pick, and now your phone is the only thing that reaches him. You text him what to do, he does it, tells you what he sees and argues back. Play is classic Zork-style exploring and puzzles, all inside a messaging-app chat, and his phone battery is your lamp. The first chapter is free.',
    // draft
    taught:
      'Writing a text-adventure parser that understands chatty phrasing ("what do you see", "go left"), and a world built entirely from data files, in one character\'s voice.',
    links: [],
    features: [],
    // draft
    lore:
      'You are the drummer of a small doom band that just bought an old building. Under a rug in the basement there is a trapdoor nobody ever opened. Dez, the band\'s runner, lost the guitarist\'s favourite pick down a crack in the floor and went after it. Now the trapdoor won\'t open, your calls don\'t go through, and the only thread left is the chat with Dez, somewhere far deeper than a basement should go.',
    development: [
      { title: 'Rooms with real light', text: 'Every room is a 3x3 grid with positioned light sources. Dez only describes what is lit, clearly or dimly, and refuses to walk into the dark.' },
      { title: 'The battery is the clock', text: 'Every action costs time and time drains Dez\'s phone. Typos and failed attempts are free, so wrong guesses never punish the player.' }
    ],
    ux: [
      { title: 'Taps for moving, typing for solving', text: 'Reply chips and tappable words cover walking and looking around, but puzzle solutions are always typed, so the shortcuts never give a puzzle away.' }
    ],
    economy: [],
    roadmap: null,
    gallery: [],
    privacy: ''
  },
  {
    slug: 'yesterday-folks',
    name: 'Yesterday Folks',
    codename: true,
    // draft
    tagline: 'A co-op town builder, still taking shape.',
    howYouPay: 'Not decided yet. It will follow the Constitution.',
    pricing: { price: 'Not decided yet', payFor: 'Will follow the Constitution' },
    platforms: ['PC VR', 'PC'],
    status: 'Early idea',
    image: null,
    imageFit: 'cover',
    // draft
    pitch:
      'Every morning the bus leaves. Get on and go gather wood, stone and fish, or stay home and turn it all into electricity, tools and defenses. At night monsters come for the heart of the town. VR is more fun, not more powerful: VR and flat-screen players get the same results and build the same town.',
    // draft
    taught:
      'Building a game ready for multiplayer from day one, where playing alone means hosting a world for one, and designing every action twice: by hand in VR, with comfortable assists on a flat screen.',
    links: [],
    features: [],
    // draft
    lore:
      'The world is a broken echo of a forgotten civilisation. The Folks are rebuilt beings, putting it back together one piece at a time, and the places tell the story themselves: abandoned buildings, strange artifacts, islands that rise in the night.',
    development: [
      { title: 'Multiplayer-ready, single-player first', text: 'Netcode for GameObjects is in from the start and the whole town lives in one saveable world model, so other players can join later without a rewrite.' },
      { title: 'One set of actions for two devices', text: 'Grab, swing and deposit are game actions, not VR code, so VR hands and mouse and keyboard trigger the same thing.' }
    ],
    ux: [],
    economy: [],
    roadmap: null,
    gallery: [],
    privacy: ''
  },
  {
    slug: 'bothmcb',
    name: 'Blood On The Heavy Metal Chess Board',
    shortName: 'BOTHMCB',
    tagline: 'Heavy metal chess puzzles: match the goal in as few moves as you can.',
    howYouPay: 'Free. Power-ups cost coins you earn by playing. Watching an ad is an optional shortcut, never forced.',
    pricing: { price: 'Free', payFor: 'Nothing. Coins are earned in play, ads are optional' },
    platforms: ['iOS', 'Android'],
    status: 'Released',
    image: require('@/assets/images/games/bothmcb-1.jpg'),
    imageFit: 'cover',
    pitch:
      'Each card is a chess puzzle: rotate, mirror and move the pieces until the board matches the goal, then get graded on your accuracy. Push deeper into the Infernal Vault, earn coins and spend them on extra moves.',
    taught:
      'Publishing on the Apple App Store: certificates, App Store Connect, screenshots and review, plus optional rewarded ads and a coin shop.',
    links: [
      { label: 'Download on the App Store', url: 'https://apps.apple.com/us/app/bothmcb-heavy-metal-chess/id6799398665' },
      { label: 'Play on Google Play', url: 'https://play.google.com/store/apps/details?id=com.sirev.bothmcbgame' }
    ],
    features: [],
    // draft
    lore: '',
    development: [],
    ux: [],
    economy: [],
    roadmap: null,
    gallery: [
      require('@/assets/images/games/bothmcb-1.jpg'),
      require('@/assets/images/games/bothmcb-2.jpg'),
      require('@/assets/images/games/bothmcb-3.jpg')
    ],
    privacy: '/Outside/BOTHMCB/privacy.html'
  },
  {
    slug: 'boxdrop',
    name: 'BoxDrop',
    tagline: 'A quick skill game about dropping boxes just right.',
    howYouPay: 'Free. Power-ups cost coins you earn by playing. Watching an ad is an optional shortcut, never forced.',
    pricing: { price: 'Free', payFor: 'Nothing. Coins are earned in play, ads are optional' },
    platforms: ['Android'],
    status: 'Released',
    image: null,
    imageFit: 'cover',
    pitch:
      'BoxDrop was my experiment project: a small skill game built to learn the whole road from idea to a live store page.',
    taught:
      'Publishing on Google Play Console end to end: store listing, testing tracks, privacy policy, and how mobile ads work in practice.',
    links: [
      { label: 'Play on Google Play', url: 'https://play.google.com/store/apps/details?id=com.florin.truckdrop' }
    ],
    features: [],
    lore: '',
    development: [],
    ux: [],
    economy: [],
    roadmap: null,
    gallery: [],
    privacy: '/Outside/BoxDrop/privacy.html'
  }
];

export default games;

export function findGame(slug) {
  return games.find((g) => g.slug === slug);
}
